import React, { useState, useEffect, useMemo } from "react";
import { Menu, MenuItem, Icon, CircularProgress, InputAdornment, TextField } from "@mui/material";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import { DROPDOWN } from "../../utils/designTokens";

// Props:
// - entities: array of objects with id and name properties
// - selectedEntityId: currently selected entity ID
// - onEntityChange: callback when entity is selected
// - loading: boolean to show loading state
// - placeholder: text to show when no entity is selected
// - icon: icon name to use (default: 'business')
// - width: width of the selector (default: '175px')
// - loadingText: text to show while loading (default: 'Loading...')

const EntitySelector = ({
                          entities = [],
                          selectedEntityId,
                          onEntityChange,
                          loading = false,
                          placeholder = "Select Item",
                          label = "",
                          icon = "business",
                          width = "225px",
                          loadingText = "Loading...",
                          emptyResultText = "No items found"
                        }) => {

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);

  // Reset search when menu closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    }
  }, [open]);

  // Handle opening and closing menu
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle entity change
  const handleChange = (entityId) => {
    onEntityChange(entityId);
    handleClose();
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Filter entities based on search term
  const filteredEntities = useMemo(() => 
    searchTerm
      ? entities.filter(entity =>
        entity.name.toLowerCase().includes(searchTerm.toLowerCase()))
      : entities,
    [entities, searchTerm]
  );

  // Get entity name
  const getEntityName = () => {
    if (loading) return loadingText;
    if (!selectedEntityId) return placeholder;
    const entity = entities.find(e => e.id === selectedEntityId);
    return entity?.name || placeholder;
  };

  return (
    <VuiBox
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 1,
        flexShrink: { xs: 1, lg: 0 },
        minWidth: 0,
        width: { xs: "100%", lg: "auto" },
        maxWidth: { xs: "100%", lg: "none" },
      }}
    >
      {label && (
        <VuiTypography
          variant={DROPDOWN.label.variant}
          color={DROPDOWN.label.color}
          fontWeight={DROPDOWN.label.fontWeight}
          sx={{
            flexShrink: 0,
            width: DROPDOWN.filterLabelColumnWidth,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </VuiTypography>
      )}
      <VuiBox
        onClick={handleClick}
        sx={{
          ...DROPDOWN.button,
          ...DROPDOWN.filterDropdownButton,
          background: DROPDOWN.background,
          backdropFilter: DROPDOWN.backdropFilter,
          border: DROPDOWN.border,
          borderRadius: DROPDOWN.borderRadius,
          "&:hover": {
            background: DROPDOWN.itemHover,
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          },
        }}
      >
        <Icon sx={{ color: "#fff", fontSize: "16px", mr: 0.5, flexShrink: 0 }}>{icon}</Icon>
        <VuiTypography
          variant="button"
          color={DROPDOWN.textColor}
          sx={{ ...DROPDOWN.text, flex: 1, minWidth: 0 }}
        >
          {getEntityName()}
        </VuiTypography>
      </VuiBox>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            backgroundColor: DROPDOWN.background,
            backdropFilter: DROPDOWN.backdropFilter,
            border: DROPDOWN.border,
            borderRadius: DROPDOWN.borderRadius,
            width: { xs: "240px", sm: "280px" },
            maxWidth: "90vw",
            padding: "16px",
            maxHeight: "400px"
          }
        }}
      >
        <VuiBox p={0.8}>
          {/* Search input */}
          <VuiBox mb={2}>
            <TextField
              fullWidth
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              sx={{
                "& .MuiInputBase-root": {
                  height: "42px",
                  borderRadius: "8px",
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: '16px' }}>search</Icon>
                  </InputAdornment>
                ),
              }}
            />
          </VuiBox>

          {/* Loading indicator or list of entities */}
          {loading ? (
            <VuiBox display="flex" justifyContent="center" alignItems="center" py={2}>
              <CircularProgress size={24} sx={{ color: DROPDOWN.selectedBg }} />
              <VuiTypography variant="button" color="text" ml={1.5}>
                {loadingText}
              </VuiTypography>
            </VuiBox>
          ) : (
            <>
              {/* Entity list */}
              <VuiBox sx={{ maxHeight: "250px", overflowY: "auto", pr: 0.5 }}>
                {filteredEntities.length > 0 ? (
                  filteredEntities.map((entity) => (
                    <MenuItem
                      key={entity.id}
                      onClick={() => handleChange(entity.id)}
                      sx={{
                        color: DROPDOWN.textColor,
                        borderRadius: '8px',
                        margin: '4px 0',
                        padding: '8px 12px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: DROPDOWN.itemHover,
                        },
                        '&.Mui-selected': {
                          color: "#fff",
                          background: DROPDOWN.selectedBg,
                          '&:hover': {
                            background: DROPDOWN.selectedHover,
                          },
                        }
                      }}
                      selected={entity.id === selectedEntityId}
                    >
                      <VuiBox display="flex" alignItems="center" width="100%">
                        <Icon sx={{
                          color: entity.id === selectedEntityId ? DROPDOWN.selectedBg : DROPDOWN.secondaryText,
                          fontSize: '16px',
                          mr: 1,
                          flexShrink: 0
                        }}>
                          {entity.id === selectedEntityId ? 'check_circle' : icon}
                        </Icon>
                        <VuiTypography
                          variant="button"
                          fontWeight={entity.id === selectedEntityId ? "bold" : "medium"}
                          color={entity.id === selectedEntityId ? DROPDOWN.textColor : "text"}
                          sx={{
                            flexGrow: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {entity.name}
                        </VuiTypography>
                      </VuiBox>
                    </MenuItem>
                  ))
                ) : (
                  <VuiBox p={2} textAlign="center">
                    <VuiTypography variant="caption" color="text">
                      {emptyResultText}
                    </VuiTypography>
                  </VuiBox>
                )}
              </VuiBox>
            </>
          )}
        </VuiBox>
      </Menu>
    </VuiBox>
  );
};

export default EntitySelector;